import { useState } from "react";
import { X, ZoomIn, FileText } from "lucide-react";
import { visualSrc, isPdf } from "@/components/PresentationVisuals";

function Figure({ v, onOpen, aspect = "aspect-[4/3]" }) {
  const src = visualSrc(v);
  return (
    <figure className="card-surface group overflow-hidden" data-testid={`presentation-visual-${v.id}`}>
      <button
        type="button"
        onClick={() => onOpen(v)}
        data-testid={`open-visual-${v.id}`}
        className="relative block w-full cursor-zoom-in overflow-hidden"
      >
        {isPdf(v) ? (
          <span className={`flex ${aspect} w-full flex-col items-center justify-center gap-3 bg-[#F1F0EC]`}>
            <FileText className="h-7 w-7 text-fog" strokeWidth={1.5} />
            <span className="text-xs text-stone">{v.title || v.filename || "Document"}</span>
          </span>
        ) : (
          <img
            src={src}
            alt={v.title || v.type}
            loading="lazy"
            className={`${aspect} w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]`}
          />
        )}
        <span className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/85 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <ZoomIn className="h-4 w-4 text-ink" strokeWidth={1.5} />
        </span>
      </button>
      {(v.title || v.caption) && (
        <figcaption className="space-y-2 p-5">
          <div className="overline">{v.type}</div>
          {v.title && <div className="font-serif text-lg leading-snug text-ink">{v.title}</div>}
          {v.caption && <p className="text-sm leading-relaxed text-stone">{v.caption}</p>}
        </figcaption>
      )}
    </figure>
  );
}

/** Adaptive design gallery: one full-width, two side by side, three or more in a grid. */
export default function PresentationGallery({ visuals = [], heading = "The Design" }) {
  const [open, setOpen] = useState(null);
  if (!visuals.length) return null;

  const layout =
    visuals.length === 1 ? "grid-cols-1" : visuals.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 xl:grid-cols-3";
  const aspect = visuals.length === 1 ? "aspect-[21/9]" : visuals.length === 2 ? "aspect-[4/3]" : "aspect-[4/3]";

  return (
    <section data-testid="presentation-gallery">
      {heading && (
        <div className="mb-7 flex items-center gap-5">
          <h2 className="font-serif text-2xl text-ink sm:text-3xl">{heading}</h2>
          <span className="h-px flex-1 bg-line" />
        </div>
      )}
      <div className={`grid gap-6 ${layout}`}>
        {visuals.map((v) => (
          <Figure key={v.id} v={v} onOpen={setOpen} aspect={aspect} />
        ))}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-sm"
          onClick={() => setOpen(null)}
          data-testid="visual-lightbox"
        >
          <button
            onClick={() => setOpen(null)}
            data-testid="close-lightbox"
            className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <figure className="max-h-full w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            {isPdf(open) ? (
              <iframe src={visualSrc(open)} title={open.title || "Document"} className="h-[80vh] w-full bg-white" />
            ) : (
              <img src={visualSrc(open)} alt={open.title || open.type} className="max-h-[80vh] w-full object-contain" />
            )}
            {(open.title || open.caption) && (
              <figcaption className="mt-5 text-center text-sm text-white/80">
                {open.title}
                {open.caption ? ` — ${open.caption}` : ""}
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </section>
  );
}
